import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import Button from '@/components/button'
import { useGetProjects } from '@/hooks/queries/use-get-projects'
import { useGetRegions } from '@/hooks/queries/use-get-regions'
import { routes } from '@/routes/config'
import { Project, Region } from '@/types/regions'

import { useStyles } from './styles'

interface SelectRegionProps {
  selectedProject: Project | null
  selectedRegion: Region | null
  onSelectionChange: (project: Project, region: Region) => void
}

export default function SelectRegion({
  selectedProject,
  selectedRegion,
  onSelectionChange,
}: SelectRegionProps) {
  const classes = useStyles()
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(true)
  const [tempSelectedProject, setTempSelectedProject] =
    useState<Project | null>(null)
  const [tempSelectedRegion, setTempSelectedRegion] = useState<Region | null>(
    null,
  )

  const { data: projects = [], isLoading: isLoadingProjects } = useGetProjects()
  const { data: regions = [], isLoading } = useGetRegions(
    tempSelectedProject?.id,
  )
  const isLoadingRegions = isLoading && !!tempSelectedProject

  const isRegionSelected = !!(selectedProject && selectedRegion)
  const isButtonDisabled = !tempSelectedProject || !tempSelectedRegion

  const projectOptions = useMemo(
    () => [{ id: -1, name: 'Cadastrar novo projeto' }, ...projects],
    [projects],
  )

  const confirmSelection = (project: Project, region: Region) => {
    setTempSelectedProject(project)
    setTempSelectedRegion(region)
    onSelectionChange(project, region)
    setIsExpanded(false)
  }

  const handleProjectChange = (
    e: React.ChangeEvent<unknown>,
    value: Project | null,
  ) => {
    setTempSelectedProject(value)
    setTempSelectedRegion(null)
  }

  const handleRegionChange = (
    e: React.ChangeEvent<unknown>,
    value: Region | null,
  ) => {
    setTempSelectedRegion(value)
  }

  const handleConfirm = () => {
    if (tempSelectedProject && tempSelectedRegion) {
      confirmSelection(tempSelectedProject, tempSelectedRegion)
    }
  }

  const handleAccordionToggle = () => {
    setIsExpanded((prev) => !prev)
  }

  const handleAccordionBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      if (selectedProject && selectedRegion) {
        if (!tempSelectedProject || !tempSelectedRegion) {
          setTempSelectedProject(selectedProject)
          setTempSelectedRegion(selectedRegion)
          setIsExpanded(false)
        }
      }
    }
  }

  const renderInputWithLoading = (
    params: any,
    placeholder: string,
    isLoading: boolean,
  ) => (
    <TextField
      {...params}
      placeholder={placeholder}
      variant='outlined'
      fullWidth
      className={classes.autocomplete}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {isLoading && <CircularProgress color='inherit' size={18} />}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  )

  useEffect(() => {
    if (isRegionSelected) {
      setTempSelectedProject(selectedProject)
      setTempSelectedRegion(selectedRegion)
      setIsExpanded(false)
    }
  }, [selectedProject, selectedRegion])

  return (
    <Accordion
      elevation={0}
      className={classes.accordion}
      expanded={isExpanded}
      onChange={handleAccordionToggle}
      onBlur={handleAccordionBlur}
    >
      <AccordionSummary
        expandIcon={<ArrowDropDownIcon />}
        className={classes.accordionSummary}
      >
        <Typography variant='body2' className={classes.projectName}>
          {isRegionSelected ? selectedProject?.name : 'Nome do Projeto'}
        </Typography>
        <Typography variant='body2' className={classes.regionName}>
          {isRegionSelected ? selectedRegion?.name : 'Nome da região'}
        </Typography>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Box className={classes.formContainer}>
          <Autocomplete
            size='small'
            aria-label='Selecione o projeto'
            disabled={isLoadingProjects}
            noOptionsText='Nenhum projeto encontrado'
            options={projectOptions}
            getOptionLabel={(option) => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            value={tempSelectedProject}
            onChange={handleProjectChange}
            renderInput={(params) =>
              renderInputWithLoading(
                params,
                'Selecione o projeto',
                isLoadingProjects,
              )
            }
            renderOption={(option) =>
              option.id < 0 ? (
                <Box
                  display='flex'
                  gridGap='1rem'
                  onClick={() =>
                    navigate(routes.ProjectAdd(), {
                      state: { from: routes.Maps() },
                    })
                  }
                >
                  <AddIcon />
                  <Typography variant='subtitle1'>{option.name}</Typography>
                </Box>
              ) : (
                option.name
              )
            }
          />

          <Autocomplete
            size='small'
            noOptionsText='Nenhuma região encontrada'
            aria-label='Selecione a região'
            options={regions}
            getOptionLabel={(option) => option.name}
            getOptionSelected={(option, value) => option.id === value.id}
            value={tempSelectedRegion}
            onChange={handleRegionChange}
            disabled={isLoadingRegions || !tempSelectedProject}
            renderInput={(params) =>
              renderInputWithLoading(
                params,
                'Selecione a região',
                isLoadingRegions,
              )
            }
          />

          <Button
            color='primary'
            onClick={handleConfirm}
            disabled={isButtonDisabled}
            className={classes.confirmButton}
          >
            Confirmar
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}
